import { motion } from 'framer-motion';
import { Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { adminService } from '@/services';
import type { PunctualityReport } from '@/types';
import { extractErrorMessage, showErrorToast } from '@/utils';
import { cn } from '@/utils/cn';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function Punctuality() {
  const now = new Date();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [punctualityData, setPunctualityData] = useState<PunctualityReport | null>(null);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await adminService.getPunctualityData(selectedYear, selectedMonth);
      setPunctualityData(data);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load punctuality data'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedYear(selectedYear + 1);
      setSelectedMonth(1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };

  const getDayOfWeek = (year: number, month: number, day: number): number => {
    return new Date(year, month - 1, day).getDay();
  };

  const getDayLabel = (dayOfWeek: number): string => {
    const labels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return labels[dayOfWeek];
  };

  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
  const maxDay = isCurrentMonth ? now.getDate() : getDaysInMonth(selectedYear, selectedMonth);

  const getDaysForMonth = (): Array<{ day: number; dayOfWeek: number; label: string }> => {
    const days: Array<{ day: number; dayOfWeek: number; label: string }> = [];
    for (let day = 1; day <= maxDay; day++) {
      const dayOfWeek = getDayOfWeek(selectedYear, selectedMonth, day);
      if (dayOfWeek !== 0) {
        days.push({ day, dayOfWeek, label: getDayLabel(dayOfWeek) });
      }
    }
    return days;
  };

  const days = getDaysForMonth();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-3">
        <div className="w-10 h-10 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
        <p className="text-slate-400 font-bold text-sm animate-pulse">
          Loading punctuality data...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Punctuality
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-1">
            Staff punctuality tracking
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePreviousMonth}
            className="p-1.5 rounded-lg hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="px-3 py-1.5 rounded-lg bg-neolab-green/10 dark:bg-white/10">
            <p className="text-xs font-black text-slate-900 dark:text-white">
              {MONTHS[selectedMonth - 1]} {selectedYear}
            </p>
          </div>
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={isCurrentMonth}
            className={cn(
              'p-1.5 rounded-lg hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors',
              isCurrentMonth && 'opacity-50 cursor-not-allowed',
            )}
          >
            <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
        </div>
      </div>

      {punctualityData?.mostPunctualStaff && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-xl glass-card dark:glass-card-dark shadow-sm border border-neolab-green/10 dark:border-white/10 bg-gradient-to-r from-neolab-green/5 to-emerald-500/5 dark:from-neolab-green/10 dark:to-emerald-500/10"
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-neolab-green/10 dark:bg-neolab-green/20">
              <Award className="w-4 h-4 text-neolab-green" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Most Punctual Staff 🎉🎉🎉🎉🎉🎉🎉🎉
              </p>
              <p className="text-sm font-black text-slate-900 dark:text-white">
                {punctualityData.mostPunctualStaff}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      <div className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                <th className="p-2 text-[9px] font-black text-slate-500 uppercase tracking-widest sticky left-0 bg-neolab-green/[0.02] dark:bg-white/5 z-10">
                  Names
                </th>
                {days.map(({ day, label }) => (
                  <th
                    key={day}
                    className="p-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center min-w-[40px]"
                  >
                    <div className="flex flex-col items-center">
                      <span>{label}</span>
                      <span className="text-[8px]">{day}</span>
                    </div>
                  </th>
                ))}
                <th className="p-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">
                  AVG
                </th>
                <th className="p-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">
                  On time
                </th>
              </tr>
            </thead>
            <tbody>
              {punctualityData?.data.map((user) => (
                <tr
                  key={user.userId}
                  className="border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors"
                >
                  <td className="p-2 font-black text-[10px] text-slate-900 dark:text-white sticky left-0 bg-white dark:bg-neolab-forest z-10">
                    {user.name}
                  </td>
                  {days.map(({ day }) => {
                    const dayData = user.dailyDifferences.find(
                      (d) => new Date(d.date).getDate() === day,
                    );
                    const diff = dayData?.difference;
                    return (
                      <td key={day} className="p-1 text-center text-[9px] font-black min-w-[40px]">
                        {diff === null || diff === undefined ? (
                          <span className="text-slate-400">N</span>
                        ) : diff < 0 ? (
                          <span className="text-emerald-600 dark:text-emerald-400">{diff}</span>
                        ) : diff > 0 ? (
                          <span className="text-rose-600 dark:text-rose-400">+{diff}</span>
                        ) : (
                          <span className="text-slate-600 dark:text-slate-400">0</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="p-2 text-center text-[9px] font-black text-slate-900 dark:text-white">
                    {user.average.toFixed(1)}
                  </td>
                  <td
                    className="p-2 text-center text-[9px] font-black text-slate-900 dark:text-white"
                    title={
                      user.eligible
                        ? undefined
                        : 'Not enough days attended to be named most punctual this month'
                    }
                  >
                    {user.onTimeDays}/{user.scheduledDays}
                    {!user.eligible && <span className="text-slate-400">*</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Punctuality;
